import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CheckCircle, CreditCard, Loader2, ArrowLeft, Stethoscope, GraduationCap, Download, MapPin, Mail } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '@/App';
import SiteHeader from '@/components/SiteHeader';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { useSiteSection } from '@/hooks/useSiteSection';

const FEATURES = [
  "Adhésion à l'association pour 1 An",
  "Référencement dans notre annuaire",
  "Accès à tous les replays",
  "Accès aux publications",
  "Accès aux rapports",
  "Accès au forum privé",
  "Accès à la messagerie interne",
];

const AdhésionPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const page = useSiteSection('page_adhesion');

  // Smooth-scroll to anchor (#ophtalmo or #intern) when arriving from menu
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      // Wait one tick for layout to settle
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 200);
    }
  }, [location.hash]);

  const handlePayment = async (planType, amount) => {
    if (!user) {
      toast.info('Veuillez vous connecter ou créer un compte pour adherer');
      navigate('/auth');
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/helloasso/checkout`, {
        amount: amount,
        subscription_type: planType
      });
      if (response.data.success && response.data.redirect_url) {
        toast.info('Redirection vers Hello Asso...');
        window.location.href = response.data.redirect_url;
      } else {
        toast.error('Erreur lors de la creation du paiement');
        setLoading(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors du paiement');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Roboto', sans-serif" }} data-testid="adhesion-page">
      <SiteHeader />
      <SEO title="Adhésion" description="Adhésion en ligne à la SFOALC. 2 formules praticiens et 1 formule interne. Paiement sécurisé via HelloAsso." />

      {/* Hero */}
      <div className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5f8a 50%, #35AFE4 100%)' }}>
        <div className="max-w-5xl mx-auto px-4 py-16 sm:py-20 relative z-10 text-center">
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3 min-h-[1.2em]" style={{ fontFamily: "'Barlow', sans-serif" }} data-testid="adhesion-title">
            {page?.title}
          </h1>
          <p className="text-white/70 text-base max-w-lg mx-auto min-h-[1.5em]" data-testid="adhesion-subtitle">
            {page?.subtitle}
          </p>
        </div>
        <svg viewBox="0 0 1440 60" preserveAspectRatio="none" className="block w-full h-[40px] relative z-10">
          <path fill="#f8f9fb" d="M0,60 L0,20 Q360,60 720,30 T1440,20 L1440,60 Z" />
        </svg>
      </div>

      <div className="bg-[#f8f9fb] py-10 sm:py-14 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-3 gap-8">

            {/* Left: Adhésion en ligne (2/3) */}
            <div className="lg:col-span-2 space-y-8">
              {/* Section header */}
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#35AFE4]/10 flex items-center justify-center">
                  <CheckCircle size={22} className="text-[#35AFE4]" />
                </div>
                <h2 className="text-xl sm:text-2xl font-bold text-[#35AFE4]" style={{ fontFamily: "'Barlow', sans-serif" }}>
                  Adhésion en ligne
                </h2>
              </div>
              <p className="text-sm text-gray-500 -mt-4 ml-[52px]">
                Adhésion en ligne via Carte Bancaire par l'intermédiaire de la solution <a href="https://www.helloasso.com" target="_blank" rel="noopener noreferrer" className="text-[#35AFE4] hover:underline font-medium">HelloAsso</a>.
              </p>

              {/* Comparison Table - Praticiens */}
              <div id="ophtalmo" className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden scroll-mt-24" data-testid="plans-table">
                {/* Table Header */}
                <div className="grid grid-cols-3 border-b border-gray-100">
                  <div className="p-4 sm:p-5 bg-[#eaf6fc] flex items-end">
                    <span className="text-sm font-bold text-[#35AFE4] uppercase tracking-wide">Type d'adhesion</span>
                  </div>
                  <div className={`p-4 sm:p-5 text-center cursor-pointer transition-all ${selectedPlan === 'soutien' ? 'bg-[#1e3a5f] text-white' : 'bg-[#35AFE4] text-white'}`}
                    onClick={() => setSelectedPlan('soutien')} data-testid="plan-soutien-header">
                    <Stethoscope size={20} className="mx-auto mb-2 opacity-80" />
                    <h3 className="text-sm sm:text-base font-bold leading-tight">Adhésion annuelle de soutien SFOALC - Praticiens</h3>
                  </div>
                  <div className={`p-4 sm:p-5 text-center cursor-pointer transition-all ${selectedPlan === 'standard' ? 'bg-[#1e3a5f] text-white' : 'bg-[#35AFE4]/80 text-white'}`}
                    onClick={() => setSelectedPlan('standard')} data-testid="plan-standard-header">
                    <Stethoscope size={20} className="mx-auto mb-2 opacity-80" />
                    <h3 className="text-sm sm:text-base font-bold leading-tight">Adhésion annuelle SFOALC - Praticiens</h3>
                  </div>
                </div>

                {/* Features row */}
                <div className="grid grid-cols-3 border-b border-gray-50">
                  <div className="p-4 sm:p-5 bg-[#eaf6fc]">
                    <span className="text-sm font-bold text-[#35AFE4]">Contenu</span>
                  </div>
                  <div className="p-4 sm:p-5 text-center border-r border-gray-50">
                    <ul className="space-y-2">
                      {FEATURES.map((f, i) => (
                        <li key={i} className="text-xs sm:text-sm text-gray-600 flex items-start gap-1.5">
                          <CheckCircle size={14} className="text-emerald-500 flex-shrink-0 mt-0.5" />
                          <span className={i === 0 ? 'font-bold text-gray-900' : ''}>{f}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="p-4 sm:p-5 text-center">
                    <ul className="space-y-2">
                      {FEATURES.map((f, i) => (
                        <li key={i} className="text-xs sm:text-sm text-gray-600 flex items-start gap-1.5">
                          <CheckCircle size={14} className="text-emerald-500 flex-shrink-0 mt-0.5" />
                          <span className={i === 0 ? 'font-bold text-gray-900' : ''}>{f}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Price row */}
                <div className="grid grid-cols-3 border-b border-gray-50">
                  <div className="p-4 sm:p-5 bg-[#eaf6fc]">
                    <span className="text-sm font-bold text-[#35AFE4]">Montant</span>
                  </div>
                  <div className="p-4 sm:p-5 text-center border-r border-gray-50">
                    <span className="text-2xl sm:text-3xl font-bold text-gray-900" data-testid="price-soutien">150,00</span>
                    <span className="text-gray-500 text-sm ml-1">EUR</span>
                  </div>
                  <div className="p-4 sm:p-5 text-center">
                    <span className="text-2xl sm:text-3xl font-bold text-gray-900" data-testid="price-standard">100,00</span>
                    <span className="text-gray-500 text-sm ml-1">EUR</span>
                  </div>
                </div>

                {/* CTA row */}
                <div className="grid grid-cols-3">
                  <div className="p-4 sm:p-5 bg-[#eaf6fc]">
                    <span className="text-sm font-bold text-[#35AFE4]">Adhésion</span>
                  </div>
                  <div className="p-4 sm:p-5 text-center border-r border-gray-50">
                    <button
                      onClick={() => handlePayment('soutien', 150)}
                      disabled={loading}
                      className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#35AFE4] hover:bg-[#1e9bd4] text-white rounded-full text-sm font-bold transition-all hover:shadow-md disabled:opacity-50"
                      data-testid="btn-pay-soutien"
                    >
                      {loading ? <Loader2 size={16} className="animate-spin" /> : <CreditCard size={16} />}
                      Adherer - 150EUR
                    </button>
                  </div>
                  <div className="p-4 sm:p-5 text-center">
                    <button
                      onClick={() => handlePayment('standard', 100)}
                      disabled={loading}
                      className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#35AFE4] hover:bg-[#1e9bd4] text-white rounded-full text-sm font-bold transition-all hover:shadow-md disabled:opacity-50"
                      data-testid="btn-pay-standard"
                    >
                      {loading ? <Loader2 size={16} className="animate-spin" /> : <CreditCard size={16} />}
                      Adherer - 100EUR
                    </button>
                  </div>
                </div>
              </div>

              {/* Intern plan */}
              <div id="intern" className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden scroll-mt-24" data-testid="plan-intern">
                <div className="grid grid-cols-3 border-b border-gray-100">
                  <div className="p-4 sm:p-5 bg-emerald-50 flex items-end">
                    <span className="text-sm font-bold text-emerald-700 uppercase tracking-wide">Internes</span>
                  </div>
                  <div className="col-span-2 p-4 sm:p-5 bg-emerald-500 text-white text-center">
                    <GraduationCap size={20} className="mx-auto mb-2 opacity-80" />
                    <h3 className="text-sm sm:text-base font-bold">Adhésion annuelle SFOALC - Internes en ophtalmologie</h3>
                  </div>
                </div>
                <div className="grid grid-cols-3 border-b border-gray-50">
                  <div className="p-4 sm:p-5 bg-emerald-50">
                    <span className="text-sm font-bold text-emerald-700">Contenu</span>
                  </div>
                  <div className="col-span-2 p-4 sm:p-5 text-center">
                    <ul className="space-y-2 inline-block text-left">
                      {FEATURES.filter((_, i) => i !== 1).map((f, i) => (
                        <li key={i} className="text-xs sm:text-sm text-gray-600 flex items-start gap-1.5">
                          <CheckCircle size={14} className="text-emerald-500 flex-shrink-0 mt-0.5" />
                          <span className={i === 0 ? 'font-bold text-gray-900' : ''}>{f}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                <div className="grid grid-cols-3 border-b border-gray-50">
                  <div className="p-4 sm:p-5 bg-emerald-50">
                    <span className="text-sm font-bold text-emerald-700">Montant</span>
                  </div>
                  <div className="col-span-2 p-4 sm:p-5 text-center">
                    <span className="text-2xl sm:text-3xl font-bold text-emerald-600" data-testid="price-intern">GRATUIT</span>
                  </div>
                </div>
                <div className="grid grid-cols-3 border-b border-gray-50">
                  <div className="p-4 sm:p-5 bg-emerald-50">
                    <span className="text-sm font-bold text-emerald-700">Condition</span>
                  </div>
                  <div className="col-span-2 p-4 sm:p-5 text-center">
                    <p className="text-sm text-gray-600">Carte étudiante en cours de validité</p>
                    <p className="text-xs text-gray-400 mt-1">Vérifiée lors de l'inscription</p>
                  </div>
                </div>
                <div className="grid grid-cols-3">
                  <div className="p-4 sm:p-5 bg-emerald-50">
                    <span className="text-sm font-bold text-emerald-700">Adhésion</span>
                  </div>
                  <div className="col-span-2 p-4 sm:p-5 text-center">
                    <button
                      onClick={() => navigate('/auth')}
                      className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-full text-sm font-bold transition-all hover:shadow-md"
                      data-testid="btn-register-intern-adhesion"
                    >
                      <GraduationCap size={16} />
                      S'inscrire gratuitement
                    </button>
                  </div>
                </div>
              </div>

              {/* Login notice */}
              {!user && (
                <div className="bg-blue-50 rounded-xl p-4 border border-blue-100 text-center">
                  <p className="text-sm text-blue-800">
                    Vous devez etre connecte pour adherer.{' '}
                    <button onClick={() => navigate('/auth')} className="font-bold underline hover:text-blue-600">
                      Se connecter / Créer un compte
                    </button>
                  </p>
                </div>
              )}
            </div>

            {/* Right sidebar: Adhésion hors ligne (1/3) */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 sticky top-24" data-testid="adhesion-hors-ligne">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                    <Mail size={20} className="text-gray-600" />
                  </div>
                  <h2 className="text-lg font-bold text-gray-900" style={{ fontFamily: "'Barlow', sans-serif" }}>
                    Adhésion HORS ligne
                  </h2>
                </div>

                <p className="text-sm text-gray-600 leading-relaxed mb-5">
                  Merci de télécharger le document ci-dessous, le completer et nous l'envoyér par courrier, accompagne de son reglement <strong>par cheque</strong>, a l'ordre de <strong>SFOALC</strong>
                </p>

                <p className="text-sm text-gray-500 mb-2">A l'adresse :</p>

                <div className="bg-gray-50 rounded-xl p-4 mb-5 space-y-1">
                  <p className="text-sm font-bold text-gray-900">Dr Marie Caroline TRONE</p>
                  <p className="text-sm text-gray-600">Service d'Ophtalmologie</p>
                  <p className="text-sm text-gray-600">Hopital Nord - CHU de Saint-Etienne</p>
                  <p className="text-sm text-gray-600">42055 Saint-Etienne Cedex 2</p>
                </div>

                <a
                  href="/api/uploads/documents/Bulletin-adhesion-2026.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full py-3 px-4 bg-[#35AFE4] hover:bg-[#1e9bd4] text-white rounded-full text-sm font-bold transition-all hover:shadow-md"
                  data-testid="btn-download-bulletin"
                >
                  <Download size={16} />
                  Télécharger le bulletin d'adhesion 2026
                </a>
              </div>
            </div>

          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AdhésionPage;
